import type {
  GemmetricFindingConfidence,
  GemmetricLiteScanFinding,
  GemmetricLiteScanResult
} from '../types/organizations';

export type OpportunityLevel = 'low' | 'moderate' | 'high' | 'significant';

export interface LiteScanSignals {
  reachable: boolean;
  httpStatus?: number;
  title: string;
  metaDescription: string;
  h1: string;
  ogTitle: string;
  ogDescription: string;
  ogSiteName: string;
  canonicalUrl: string;
  jsonLdCount: number;
  hasOrganization: boolean;
  hasLocalBusiness: boolean;
  hasSameAsLinks: boolean;
  hasContactSignals: boolean;
  robotsStatus: 'accessible' | 'inaccessible' | 'unknown';
  blocksBroadCrawl: boolean;
}

export interface EnrichedLiteScanFinding extends GemmetricLiteScanFinding {
  businessLabel: string;
  whyItMatters: string;
  confidence: GemmetricFindingConfidence;
}

export interface LiteScanSnapshot {
  strengths: string[];
  opportunities: string[];
}

export interface LiteScanScoreInterpretation {
  tier: string;
  description: string;
  opportunityLevel: OpportunityLevel;
}

type FindingCopy = {
  label: string;
  businessLabel: string;
  confidence: GemmetricFindingConfidence;
  passWhy: string;
  warnWhy: string;
  failWhy?: string;
  unknownWhy?: string;
  strengthPass?: string;
  opportunityWarn?: string;
  opportunityFail?: string;
  opportunityUnknown?: string;
  prospectingWarn?: string;
  prospectingFail?: string;
};

const FINDING_COPY: Record<string, FindingCopy> = {
  reachable: {
    label: 'Public website availability',
    businessLabel: 'Website accessibility',
    confidence: 'observed',
    passWhy: 'The public website loads, so both customers and automated systems can reach business information.',
    warnWhy: 'Website availability could not be fully confirmed during this quick check.',
    failWhy: 'The website could not be reached, which limits how AI systems can learn about the business.',
    strengthPass: 'Public website is reachable for customers and automated review',
    opportunityFail: 'Website could not be reached during this initial check'
  },
  ai_crawler_access: {
    label: 'AI crawler accessibility',
    businessLabel: 'AI crawler accessibility',
    confidence: 'observed',
    passWhy: 'Automated systems appear able to access public pages without broad crawl blocks.',
    warnWhy: 'Crawl guidance could not be fully verified, adding uncertainty for automated discovery.',
    failWhy: 'Broad crawl restrictions may limit how AI systems can review public business information.',
    unknownWhy: 'Crawl guidance could not be verified during this initial check.',
    strengthPass: 'No broad crawl restrictions detected for automated discovery',
    opportunityWarn: 'AI crawler access could not be fully verified',
    opportunityFail: 'AI crawler access may be limited for automated discovery',
    prospectingWarn: 'AI systems may have a harder time consistently reviewing this website.'
  },
  entity_identity: {
    label: 'Business identity clarity',
    businessLabel: 'Business identity clarity',
    confidence: 'heuristic',
    passWhy: 'The homepage presents a recognizable business name, which helps AI systems treat this as a distinct entity.',
    warnWhy: 'Business identity signals appear limited, which may make AI naming and matching less consistent.',
    failWhy: 'A clear business identity signal was not detected on the homepage.',
    strengthPass: 'Business identity appears clear on the homepage',
    opportunityWarn: 'Business identity clarity appears limited for AI systems',
    prospectingWarn: 'AI systems may have difficulty understanding this business consistently.'
  },
  entity_recognition: {
    label: 'Entity recognition confidence',
    businessLabel: 'Entity recognition confidence',
    confidence: 'heuristic',
    passWhy: 'Multiple identity signals suggest AI systems can distinguish this business as a specific organization.',
    warnWhy: 'Entity recognition signals appear partial, which may reduce confidence when AI systems match this business.',
    failWhy: 'Entity recognition signals appear weak across the homepage.',
    strengthPass: 'Entity recognition signals appear reasonably strong',
    opportunityWarn: 'Entity recognition confidence appears limited',
    prospectingWarn: 'Entity recognition signals could be strengthened for AI discovery.'
  },
  business_description: {
    label: 'Business description consistency',
    businessLabel: 'Business description consistency',
    confidence: 'strong',
    passWhy: 'Search engines and AI systems have clear descriptive information about what this business does.',
    warnWhy: 'Business descriptions appear thin or inconsistent, which may affect how AI systems summarize the company.',
    failWhy: 'A clear business description was not detected for AI and search systems to use.',
    strengthPass: 'Clear descriptive information is available for AI and search systems',
    opportunityWarn: 'Business description consistency may be weak across discovery channels',
    prospectingWarn: 'Business description signals may not be consistent enough for reliable AI summaries.'
  },
  brand_discoverability: {
    label: 'Brand discoverability indicators',
    businessLabel: 'Brand discoverability indicators',
    confidence: 'heuristic',
    passWhy: 'Brand-sharing signals are present, which can help AI systems connect this business across channels.',
    warnWhy: 'Brand discoverability signals appear incomplete, which may weaken cross-channel recognition.',
    failWhy: 'Brand discoverability signals were not detected on the homepage.',
    strengthPass: 'Brand discoverability signals appear present',
    opportunityWarn: 'Brand discoverability signals may be incomplete',
    prospectingWarn: 'Brand discoverability signals could be strengthened for AI discovery.'
  },
  structured_entity_data: {
    label: 'AI-readable business data',
    businessLabel: 'AI-readable business data',
    confidence: 'observed',
    passWhy: 'Structured data gives AI systems machine-readable context about the business beyond plain text.',
    warnWhy: 'Structured business data was not detected, so AI retrieval may rely on weaker page text alone.',
    strengthPass: 'Structured business data is available for AI systems to parse',
    opportunityWarn: 'Structured business information appears incomplete',
    prospectingWarn: 'Structured business information appears incomplete for AI retrieval.'
  },
  organization_identity: {
    label: 'Organization identity signals',
    businessLabel: 'Organization identity signals',
    confidence: 'strong',
    passWhy: 'Organization-level structured signals help AI systems identify this business as a distinct company.',
    warnWhy: 'Organization identity signals appear incomplete, which may weaken entity matching in AI answers.',
    strengthPass: 'Organization identity signals are present',
    opportunityWarn: 'Organization identity signals may be incomplete',
    prospectingWarn: 'Organization identity signals could be strengthened for AI discovery.'
  },
  local_entity_consistency: {
    label: 'Local entity consistency',
    businessLabel: 'Local entity consistency',
    confidence: 'heuristic',
    passWhy: 'Local business signals appear aligned, which supports location-aware AI recommendations.',
    warnWhy: 'Local entity signals appear partial, which may reduce confidence in location-based AI answers.',
    strengthPass: 'Local entity signals appear reasonably consistent',
    opportunityWarn: 'Local visibility signals may not be fully optimized',
    prospectingWarn: 'Local visibility signals may not be fully optimized for AI discovery.'
  },
  knowledge_graph_readiness: {
    label: 'Knowledge graph readiness',
    businessLabel: 'Knowledge graph readiness',
    confidence: 'heuristic',
    passWhy: 'Signals suggest this business may be easier to connect within broader knowledge and entity graphs.',
    warnWhy: 'Knowledge graph readiness appears limited based on public homepage signals.',
    strengthPass: 'Knowledge graph readiness signals appear present',
    opportunityWarn: 'Knowledge graph readiness appears limited',
    prospectingWarn: 'Knowledge graph readiness signals appear limited for AI entity mapping.'
  },
  ai_retrieval_readiness: {
    label: 'AI retrieval readiness',
    businessLabel: 'AI retrieval readiness',
    confidence: 'heuristic',
    passWhy: 'Core public signals suggest AI systems can retrieve meaningful information about this business.',
    warnWhy: 'AI retrieval readiness appears partial and may produce inconsistent answers about this business.',
    failWhy: 'AI retrieval readiness appears weak because foundational public signals are missing or blocked.',
    strengthPass: 'AI retrieval readiness signals appear reasonably strong',
    opportunityWarn: 'AI retrieval readiness appears partial',
    opportunityFail: 'AI retrieval readiness appears weak',
    prospectingWarn: 'AI systems may retrieve incomplete or inconsistent information about this business.'
  },
  citation_readiness: {
    label: 'AI citation readiness',
    businessLabel: 'AI citation readiness',
    confidence: 'heuristic',
    passWhy: 'Descriptive homepage signals may help AI systems cite and summarize this business accurately.',
    warnWhy: 'Citation-ready descriptive signals appear limited, which may affect how confidently AI systems reference this business.',
    strengthPass: 'Descriptive signals may support accurate AI citations',
    opportunityWarn: 'AI citation readiness appears limited',
    prospectingWarn: 'AI citation readiness signals appear limited for confident referencing.'
  }
};

/** Importance multiplier when converting finding health into opportunity score */
const FINDING_WEIGHTS: Record<string, number> = {
  reachable: 1.6,
  ai_retrieval_readiness: 1.5,
  ai_crawler_access: 1.3,
  entity_identity: 1.2,
  entity_recognition: 1.15,
  business_description: 1.1,
  structured_entity_data: 1.1,
  organization_identity: 1.05,
  local_entity_consistency: 1.05,
  knowledge_graph_readiness: 1.0,
  brand_discoverability: 0.95,
  citation_readiness: 0.9
};

const HEALTH_POINTS: Record<GemmetricLiteScanFinding['status'], number> = {
  pass: 11,
  warn: 4,
  fail: 0,
  unknown: 2
};

const CRITICAL_FAIL_BONUS: Partial<Record<string, number>> = {
  reachable: 42,
  ai_crawler_access: 18,
  ai_retrieval_readiness: 16
};

const GENERIC_TITLE_RE = /^(home|welcome|homepage|index|untitled|default)$/i;

const isDescriptiveTitle = (title: string): boolean => {
  const trimmed = title.trim();
  return trimmed.length >= 12 && trimmed.length <= 90 && !GENERIC_TITLE_RE.test(trimmed);
};

const hasMeaningfulDescription = (value: string): boolean => value.trim().length >= 40;

const deriveEntityIdentityStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  if (!signals.title.trim()) return 'fail';
  if (isDescriptiveTitle(signals.title) && (signals.h1.trim() || signals.ogSiteName.trim())) return 'pass';
  if (signals.title.trim().length >= 8) return 'warn';
  return 'fail';
};

const deriveEntityRecognitionStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  if (signals.hasOrganization && isDescriptiveTitle(signals.title)) return 'pass';
  if (signals.hasOrganization || isDescriptiveTitle(signals.title)) return 'warn';
  return 'fail';
};

const deriveBusinessDescriptionStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  const description = signals.metaDescription.trim() || signals.ogDescription.trim();
  if (hasMeaningfulDescription(description)) return 'pass';
  if (description.length >= 15) return 'warn';
  return 'warn';
};

const deriveBrandDiscoverabilityStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  const ogSignals = [signals.ogTitle, signals.ogDescription, signals.ogSiteName].filter((v) => v.trim()).length;
  if (ogSignals >= 3) return 'pass';
  if (ogSignals >= 1) return 'warn';
  return 'warn';
};

const deriveLocalEntityStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  if (signals.hasLocalBusiness && signals.hasContactSignals) return 'pass';
  if (signals.hasLocalBusiness || signals.hasContactSignals) return 'warn';
  return 'warn';
};

const deriveKnowledgeGraphStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  if (signals.hasOrganization && signals.hasSameAsLinks) return 'pass';
  if (signals.hasOrganization || signals.jsonLdCount >= 2) return 'warn';
  return 'warn';
};

const deriveAiRetrievalStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  if (!signals.reachable) return 'fail';
  const hasDescriptiveLayer =
    hasMeaningfulDescription(signals.metaDescription) ||
    signals.jsonLdCount > 0 ||
    isDescriptiveTitle(signals.title);
  if (!signals.blocksBroadCrawl && hasDescriptiveLayer) return 'pass';
  if (hasDescriptiveLayer) return 'warn';
  return 'fail';
};

const deriveCitationReadinessStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  const description = signals.metaDescription.trim() || signals.ogDescription.trim();
  if (hasMeaningfulDescription(description) && isDescriptiveTitle(signals.title)) return 'pass';
  if (description.length >= 20 || isDescriptiveTitle(signals.title)) return 'warn';
  return 'warn';
};

const deriveAiCrawlerStatus = (signals: LiteScanSignals): GemmetricLiteScanFinding['status'] => {
  if (signals.blocksBroadCrawl) return 'fail';
  if (signals.robotsStatus === 'accessible') return 'pass';
  if (signals.robotsStatus === 'inaccessible') return 'warn';
  return 'unknown';
};

export const getOpportunityScoreInterpretation = (score: number): LiteScanScoreInterpretation => {
  const normalized = Math.max(0, Math.min(100, Math.round(score)));
  if (normalized <= 22) {
    return {
      tier: 'Strong Visibility Foundation',
      description: 'Few meaningful AI visibility gaps surfaced in this initial assessment.',
      opportunityLevel: 'low'
    };
  }
  if (normalized <= 48) {
    return {
      tier: 'Moderate Opportunity',
      description: 'Some AI visibility improvements may be worth exploring in a sales conversation.',
      opportunityLevel: 'moderate'
    };
  }
  if (normalized <= 74) {
    return {
      tier: 'High Opportunity',
      description: 'Multiple preliminary indicators suggest meaningful AI visibility upside.',
      opportunityLevel: 'high'
    };
  }
  return {
    tier: 'Significant Opportunity',
    description: 'Several preliminary indicators suggest strong follow-up potential for visibility infrastructure.',
    opportunityLevel: 'significant'
  };
};

export const scoreFromFindings = (
  findings: GemmetricLiteScanFinding[]
): { opportunityScore: number; opportunityLevel: OpportunityLevel } => {
  let health = 0;
  let criticalBonus = 0;

  findings.forEach((finding) => {
    const weight = FINDING_WEIGHTS[finding.id] ?? 1;
    health += (HEALTH_POINTS[finding.status] ?? 2) * weight;
    if (finding.status === 'fail') {
      criticalBonus += CRITICAL_FAIL_BONUS[finding.id] ?? 0;
    }
  });

  const rawOpportunity = 100 - health + criticalBonus;
  const opportunityScore = Math.max(0, Math.min(100, Math.round(rawOpportunity)));
  return {
    opportunityScore,
    opportunityLevel: getOpportunityScoreInterpretation(opportunityScore).opportunityLevel
  };
};

const whyForStatus = (
  copy: FindingCopy,
  status: GemmetricLiteScanFinding['status']
): string => {
  if (status === 'pass') return copy.passWhy;
  if (status === 'fail') return copy.failWhy || copy.warnWhy;
  if (status === 'unknown') return copy.unknownWhy || copy.warnWhy;
  return copy.warnWhy;
};

const enrichFinding = (finding: GemmetricLiteScanFinding): EnrichedLiteScanFinding => {
  const copy = FINDING_COPY[finding.id];
  if (!copy) {
    return {
      ...finding,
      businessLabel: finding.label,
      whyItMatters: finding.detail || 'Preliminary indicator from the initial website check.',
      confidence: finding.confidence || 'heuristic'
    };
  }
  return {
    ...finding,
    label: copy.label,
    businessLabel: copy.businessLabel,
    whyItMatters: whyForStatus(copy, finding.status),
    confidence: copy.confidence
  };
};

export const buildSnapshotFromFindings = (findings: GemmetricLiteScanFinding[]): LiteScanSnapshot => {
  const strengths: string[] = [];
  const opportunities: string[] = [];

  findings.forEach((finding) => {
    const copy = FINDING_COPY[finding.id];
    if (!copy) return;

    if (finding.status === 'pass') {
      if (copy.strengthPass && !strengths.includes(copy.strengthPass)) {
        strengths.push(copy.strengthPass);
      }
      return;
    }

    const opportunityText =
      finding.status === 'fail'
        ? copy.opportunityFail
        : finding.status === 'unknown'
          ? copy.opportunityUnknown
          : copy.opportunityWarn;

    if (opportunityText && !opportunities.includes(opportunityText)) {
      opportunities.push(opportunityText);
    }
  });

  return { strengths, opportunities };
};

export const buildProspectingInsights = (
  findings: GemmetricLiteScanFinding[],
  snapshot: LiteScanSnapshot,
  opportunityScore: number
): string[] => {
  const insights: string[] = [];

  if (opportunityScore >= 75) {
    insights.push('This account may be worth prioritizing for an AI visibility infrastructure conversation.');
  } else if (opportunityScore >= 50) {
    insights.push('Preliminary signals suggest meaningful AI visibility opportunities worth exploring on a call.');
  } else if (opportunityScore <= 22) {
    insights.push('Public signals look relatively strong; pursue only if strategic fit or relationship value is high.');
  }

  findings.forEach((finding) => {
    const copy = FINDING_COPY[finding.id];
    if (!copy || finding.status === 'pass') return;
    const line =
      finding.status === 'fail' ? copy.prospectingFail : copy.prospectingWarn;
    if (line && !insights.includes(line)) insights.push(line);
  });

  snapshot.opportunities.slice(0, 2).forEach((item) => {
    const normalized = item.endsWith('.') ? item : `${item}.`;
    if (!insights.some((existing) => existing.includes(item))) {
      insights.push(normalized);
    }
  });

  return insights.slice(0, 4);
};

export const buildSummaryFromSnapshot = (
  reachable: boolean,
  snapshot: LiteScanSnapshot,
  opportunityScore: number
): string => {
  if (!reachable) {
    return 'Initial assessment could not fully review the website. Follow-up may still be worthwhile if strategic interest is high.';
  }

  const strengthCount = snapshot.strengths.length;
  const opportunityCount = snapshot.opportunities.length;
  const interpretation = getOpportunityScoreInterpretation(opportunityScore);

  if (opportunityScore <= 22 && strengthCount > 0) {
    return `Initial assessment found ${strengthCount} positive AI visibility signal${strengthCount === 1 ? '' : 's'} with limited upside (${interpretation.tier.toLowerCase()}).`;
  }
  if (opportunityCount > 0 && strengthCount > 0) {
    return `Initial assessment found ${strengthCount} positive signal${strengthCount === 1 ? '' : 's'} and ${opportunityCount} AI visibility opportunity area${opportunityCount === 1 ? '' : 's'} worth discussing.`;
  }
  if (opportunityCount > 0) {
    return `Initial assessment identified ${opportunityCount} AI visibility opportunity area${opportunityCount === 1 ? '' : 's'} for a prospecting conversation.`;
  }
  return 'Initial assessment completed with limited public AI visibility signals to review.';
};

const pushFinding = (
  findings: GemmetricLiteScanFinding[],
  id: string,
  status: GemmetricLiteScanFinding['status'],
  detail?: string
) => {
  const copy = FINDING_COPY[id];
  findings.push({
    id,
    label: copy?.label || id,
    status,
    detail
  });
};

export const buildLiteScanReport = (signals: LiteScanSignals): GemmetricLiteScanResult => {
  const findings: GemmetricLiteScanFinding[] = [];

  pushFinding(
    findings,
    'reachable',
    signals.reachable ? 'pass' : 'fail',
    signals.httpStatus ? `HTTP ${signals.httpStatus}` : undefined
  );

  if (signals.reachable) {
    pushFinding(
      findings,
      'entity_identity',
      deriveEntityIdentityStatus(signals),
      signals.title ? signals.title.slice(0, 120) : 'No clear business name detected'
    );
    pushFinding(findings, 'entity_recognition', deriveEntityRecognitionStatus(signals));
    pushFinding(
      findings,
      'business_description',
      deriveBusinessDescriptionStatus(signals),
      signals.metaDescription
        ? signals.metaDescription.slice(0, 160)
        : signals.ogDescription
          ? signals.ogDescription.slice(0, 160)
          : 'No descriptive summary detected'
    );
    pushFinding(findings, 'brand_discoverability', deriveBrandDiscoverabilityStatus(signals));
    pushFinding(
      findings,
      'structured_entity_data',
      signals.jsonLdCount > 0 ? 'pass' : 'warn',
      signals.jsonLdCount > 0 ? `${signals.jsonLdCount} structured data block(s)` : 'No structured data detected'
    );
    pushFinding(
      findings,
      'organization_identity',
      signals.hasOrganization ? 'pass' : 'warn'
    );
    pushFinding(findings, 'local_entity_consistency', deriveLocalEntityStatus(signals));
    pushFinding(findings, 'knowledge_graph_readiness', deriveKnowledgeGraphStatus(signals));
    pushFinding(findings, 'ai_retrieval_readiness', deriveAiRetrievalStatus(signals));
    pushFinding(findings, 'citation_readiness', deriveCitationReadinessStatus(signals));
  }

  pushFinding(
    findings,
    'ai_crawler_access',
    deriveAiCrawlerStatus(signals),
    signals.blocksBroadCrawl
      ? 'robots.txt may block broad crawling'
      : signals.robotsStatus === 'accessible'
        ? 'robots.txt accessible'
        : signals.robotsStatus === 'inaccessible'
          ? 'robots.txt was not accessible'
          : 'Could not fetch robots.txt'
  );

  const enrichedFindings = findings.map(enrichFinding);
  const snapshot = buildSnapshotFromFindings(findings);
  const { opportunityScore, opportunityLevel } = scoreFromFindings(findings);
  const prospectingInsights = buildProspectingInsights(findings, snapshot, opportunityScore);
  const warnings = snapshot.opportunities.filter((item) =>
    /could not be reached|crawl access|retrieval readiness appears weak/i.test(item)
  );
  const observations = snapshot.opportunities;
  const summary = buildSummaryFromSnapshot(signals.reachable, snapshot, opportunityScore);

  return {
    websiteUrl: '',
    scannedAt: new Date().toISOString(),
    reachable: signals.reachable,
    opportunityScore,
    opportunityLevel,
    snapshot,
    findings: enrichedFindings,
    warnings,
    observations,
    prospectingInsights,
    summary
  };
};

export const resolveOpportunityLevel = (
  scan: GemmetricLiteScanResult
): OpportunityLevel => {
  if (scan.opportunityLevel) return scan.opportunityLevel;
  if (scan.visibilityRisk === 'high') return 'significant';
  if (scan.visibilityRisk === 'medium') return 'moderate';
  if (scan.visibilityRisk === 'low') return 'low';
  if (typeof scan.opportunityScore === 'number') {
    return getOpportunityScoreInterpretation(scan.opportunityScore).opportunityLevel;
  }
  return 'moderate';
};

export const formatFindingConfidenceLabel = (confidence?: GemmetricFindingConfidence): string => {
  if (confidence === 'observed') return 'Verified';
  if (confidence === 'strong') return 'Strong indicator';
  return 'Preliminary';
};

export const enrichLiteScanForDisplay = (
  scan: GemmetricLiteScanResult
): GemmetricLiteScanResult & {
  scoreInterpretation: LiteScanScoreInterpretation;
  displaySnapshot: LiteScanSnapshot;
} => {
  const findings = (scan.findings || []).map((f) =>
    'whyItMatters' in f && (f as EnrichedLiteScanFinding).whyItMatters
      ? (f as EnrichedLiteScanFinding)
      : enrichFinding(f)
  );
  const displaySnapshot =
    scan.snapshot && (scan.snapshot.strengths.length > 0 || scan.snapshot.opportunities.length > 0)
      ? scan.snapshot
      : buildSnapshotFromFindings(findings);
  const opportunityScore =
    typeof scan.opportunityScore === 'number'
      ? scan.opportunityScore
      : scoreFromFindings(findings).opportunityScore;
  const scoreInterpretation = getOpportunityScoreInterpretation(opportunityScore);
  const opportunityLevel = scan.opportunityLevel || resolveOpportunityLevel({ ...scan, opportunityScore });
  const summary =
    scan.summary && !scan.summary.includes('Lite Scan found')
      ? scan.summary
      : buildSummaryFromSnapshot(scan.reachable, displaySnapshot, opportunityScore);
  const prospectingInsights =
    scan.prospectingInsights && scan.prospectingInsights.length > 0
      ? scan.prospectingInsights
      : buildProspectingInsights(findings, displaySnapshot, opportunityScore);

  return {
    ...scan,
    opportunityScore,
    opportunityLevel,
    snapshot: displaySnapshot,
    findings,
    summary,
    prospectingInsights,
    scoreInterpretation,
    displaySnapshot
  };
};

export const formatOpportunityLevelLabel = (level: OpportunityLevel): string => {
  const labels: Record<OpportunityLevel, string> = {
    low: 'Opportunity Low',
    moderate: 'Opportunity Moderate',
    high: 'Opportunity High',
    significant: 'Opportunity Significant'
  };
  return labels[level];
};
